import { useEffect, useState, useCallback, useRef } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../config';

/**
 * RestoreUndoBanner — Mini barra flotante superior que aparece tras restaurar
 * un snapshot. Permite "Deshacer" la reversión durante 30 segundos restaurando
 * un snapshot pre-restore que se capturó automáticamente antes del cambio.
 *
 * Escucha:
 *   damyxia:snapshot-restored  →  detail: { summary, timestamp, undoSnapshotId }
 *
 * No interfiere con el resto del flujo: si no hay undoSnapshotId, no se muestra.
 */
export default function RestoreUndoBanner() {
  const { language } = useLanguage();
  const es = language !== 'en';

  const [open, setOpen] = useState(false);
  const [restoredAt, setRestoredAt] = useState('');
  const [undoId, setUndoId] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const timerRef = useRef(null);

  const formatDate = useCallback((ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      return d.toLocaleString(es ? 'es-ES' : 'en-US', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return ts;
    }
  }, [es]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      // Sólo mostramos la barra si tenemos un snapshot pre-restore para deshacer.
      if (!detail.undoSnapshotId) return;
      setRestoredAt(detail.timestamp || '');
      setUndoId(detail.undoSnapshotId);
      setOpen(true);
      // Auto-ocultar a los 30s.
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setOpen(false), 30000);
    };
    window.addEventListener('damyxia:snapshot-restored', handler);
    return () => {
      window.removeEventListener('damyxia:snapshot-restored', handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleUndo = async () => {
    if (!undoId || undoing) return;
    setUndoing(true);
    try {
      const res = await fetch(`${API_URL}/api/snapshots/${undoId}/restore`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.ok) {
        toast.success(es ? 'Reversión deshecha. Estado anterior restaurado.' : 'Revert undone. Previous state restored.', { duration: 4000 });
        // Notificar al resto de la app sin emitir nuevo undoSnapshotId
        // (evita banner recursivo "deshacer el deshacer").
        try {
          window.dispatchEvent(new CustomEvent('damyxia:snapshot-restored', {
            detail: { summary: '(undo)', timestamp: '' },
          }));
        } catch (_) { /* noop */ }
        setOpen(false);
      } else {
        toast.error(es ? 'No se pudo deshacer la reversión.' : 'Could not undo the revert.');
      }
    } catch {
      toast.error(es ? 'No se pudo deshacer la reversión.' : 'Could not undo the revert.');
    } finally {
      setUndoing(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[10001] px-3 py-2 sm:px-4 sm:py-2.5 rounded-full bg-zinc-900/95 border border-amber-500/40 shadow-lg shadow-amber-500/10 flex items-center gap-2.5 sm:gap-3 max-w-[94vw]"
      role="status"
      aria-live="polite"
      data-testid="restore-undo-banner"
    >
      <span className="text-xs sm:text-sm text-amber-200 truncate">
        {es ? 'Restaurado al' : 'Restored to'} ⏱ {formatDate(restoredAt) || '—'}
      </span>
      <button
        type="button"
        onClick={handleUndo}
        disabled={undoing}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        data-testid="restore-undo-btn"
      >
        <RotateCcw className={`w-3.5 h-3.5 ${undoing ? 'animate-spin' : ''}`} />
        {undoing
          ? (es ? 'Deshaciendo...' : 'Undoing...')
          : (es ? 'Deshacer' : 'Undo')}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="p-1 rounded-full hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors shrink-0"
        aria-label={es ? 'Cerrar' : 'Close'}
        data-testid="restore-undo-close-btn"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
